/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable react/no-array-index-key */
import React, { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    Button, Loader, Title, Checkbox,
    Infobox,
} from 'ui-library';
import Divider from 'ui-library/components/Divider';
import { THREEBTYPES } from 'constants/constants';
import DocBox from 'components/DocBox';
import { useCaseDetails } from 'datasource/CaseManagement/CaseDetailsManager/CaseDetailsManager';
import { ApplicationStatus, SubmittedCaseOrderStatus } from 'components/DocBox/DocBox.types';
import IconWithTooltip from 'components/common/IconWithTooltip';

import { ICON_INFO_OUTLINE } from 'ui-library/components/Icon';

const SingleCaseCompSubmitted: FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [isDocReUploadInProgress, setIsDocReUploadInProgress] = useState(false);
    const [isSignatureVerifiedInProgress, setIsSignatureVerifiedInProgress] = useState(false);
    const {
        status,
        type,
        productName,
        productNameTranslated,
        productExternalId,
        advisor,
        createdBy,
        submitted,
        submittedBy,
        depotNumber,
        documents,
        rubrik,
        onManualSign,
        onESign,
        onDocGeneration,
        setRetryState,
        acceptDocumentSend,
        isLoading,
        handleDocReUpload,
        isSignatureVerified,
        handleVerifySignature,
        dfsClientId,
        portfolioId,
        lpzCaseId,
        createdOn,
        orderType,
        isEmailRetention,
        submittedCase,
        reasonForSale,
        reasonText,
        fromDL,
    } = useCaseDetails();

    const onDocReUpload = useCallback(
        async (documentId) => {
            setIsDocReUploadInProgress(true);
            try {
                await handleDocReUpload(documentId);
            } finally {
                setIsDocReUploadInProgress(false);
            }
        },
        [handleDocReUpload],
    );

    const onSignatureVerify = useCallback(async () => {
        setIsSignatureVerifiedInProgress(true);
        try {
            await handleVerifySignature();
        } finally {
            setIsSignatureVerifiedInProgress(false);
        }
    }, [handleVerifySignature]);

    const handleViewTransactions = useCallback(() => {
        if (dfsClientId && portfolioId) {
            navigate(
                `/client/${dfsClientId}/portfolios/${portfolioId}/tab/stock`,
            );
        }
    }, [dfsClientId, portfolioId]);

    const isPK = productExternalId === THREEBTYPES.pk;

    return (
        <>
            {status === SubmittedCaseOrderStatus.aborted && !fromDL && (
                <Infobox info icon={ICON_INFO_OUTLINE} className="mb-32">
                    {t('CaseDetails.SingleCase.Info.CancelledCase')}
                </Infobox>
            )}
            <div className="case-details__content">
                <div
                    className={`single-case-section${status === SubmittedCaseOrderStatus.aborted ? ' single-case-section-cancelled' : ''}`}
                >
                    {isLoading ? (
                        <Loader />
                    ) : (
                        <>
                            <Title type="3">
                                <strong>{t(type)}</strong>
                                {' '}
                                {!isPK ? `${productNameTranslated} -` : ''}
                                {' '}
                                {productName}
                                {rubrik ? ` <${rubrik}>` : ' '}
                            </Title>

                            <div className="case--row">
                                <div className="case-info-grid">
                                    <div className="case-info-grid-col">
                                        <Title type="5">
                                            {t(
                                                'CreateCase.CasesInProgress.Table.LPZCaseId',
                                            )}
                                        </Title>
                                        {lpzCaseId}
                                    </div>
                                    {fromDL ? (
                                        <div className="case-info-grid-col">
                                            <Title type="5">
                                                {t(
                                                    'ApplicationDetails.AdvisorySection.ProductApplications.Submitted',
                                                )}
                                            </Title>
                                            {submitted || '-'}
                                        </div>
                                    ) : (
                                        <div className="case-info-grid-col">
                                            <Title type="5">
                                                {t(
                                                    'ApplicationDetails.AdvisorySection.ProductApplications.Created',
                                                )}
                                            </Title>
                                            {createdOn}
                                        </div>
                                    )}
                                    <Divider direction="vertical" />
                                    {!fromDL && (
                                        <div className="case-info-grid-col">
                                            <Title type="5">
                                                {t(
                                                    'ApplicationDetails.AdvisorySection.Advisor',
                                                )}
                                            </Title>
                                            {advisor}
                                        </div>
                                    )}
                                    <div className="case-info-grid-col">
                                        <Title type="5">
                                            {t(
                                                'ApplicationDetails.AdvisorySection.ProductApplications.CreatedBy',
                                            )}
                                        </Title>
                                        {createdBy}
                                    </div>
                                    <Divider direction="vertical" />

                                    {/* Columns only shown if case type is CHANGE || SELL || BUY */}
                                    <div className="case-info-grid-col">
                                        <Title type="5">
                                            {t(
                                                'ApplicationDetails.AdvisorySection.ProductApplications.DepotNumber',
                                            )}
                                        </Title>
                                        {depotNumber || '-'}
                                    </div>

                                    {/* Columns only shown if case type is SELL || BUY */}
                                    <div className="case-info-grid-col">
                                        <Title type="5">
                                            {t(
                                                'CreateCase.CasesInProgress.Table.Type',
                                            )}
                                        </Title>
                                        {orderType}
                                    </div>

                                    {/* Columns only shown if case type is SELL */}
                                    {fromDL ? (
                                        <>
                                            <Divider direction="vertical" />
                                            <div className="case-info-grid-col">
                                                <Title type="5">
                                                    {t('portfolios.columns.status')}
                                                </Title>
                                                {t(`DL.ViewCases.Status.${status}`)}
                                            </div>

                                            {status !== SubmittedCaseOrderStatus.aborted && (
                                                <div className="case-info-grid-col align-end ml-auto">
                                                    <Button
                                                        type="link"
                                                        className="view-transactions-button"
                                                        onClick={handleViewTransactions}
                                                    >
                                                        {t(
                                                            'CaseDetails.Link.ViewTransactions.Label',
                                                        )}
                                                    </Button>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <div className="case-info-grid-col">
                                                <Title type="5">
                                                    {t(
                                                        'CreateNewCase.Summary.Title.ReasonForSale',
                                                    )}
                                                </Title>
                                                <div className="flex align-center gap-4">
                                                    {reasonForSale}
                                                    {' '}
                                                    {reasonText ? (
                                                        <IconWithTooltip
                                                            description={reasonText}
                                                        />
                                                    ) : (
                                                        ''
                                                    )}
                                                </div>
                                            </div>
                                            <Divider direction="vertical" />

                                            <div className="case-info-grid-col">
                                                <Title type="5">
                                                    {t(
                                                        'CaseDetails.Retention.Label',
                                                    )}
                                                </Title>
                                                <Checkbox
                                                    id="retention-verified"
                                                    checked={isEmailRetention}
                                                    disabled
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>

                                {!fromDL && (
                                    <div className="case-footer-grid">
                                        <div className="single-case--grid-col">
                                            {documents?.map((document, index) => (
                                                <DocBox
                                                    isSentCase={false}
                                                    key={`${document?.documentName?.replaceAll(
                                                        ' ',
                                                        '_',
                                                    )}_${index}`}
                                                    type={document.type}
                                                    status={ApplicationStatus.submitted}
                                                    uploadStatus={document?.status}
                                                    documentName={
                                                        document?.documentName
                                                    }
                                                    documentId={
                                                        document?.documentId
                                                    }
                                                    allowSigning
                                                    canESign
                                                    onManualSign={onManualSign}
                                                    onESign={onESign}
                                                    onDocGeneration={
                                                        onDocGeneration
                                                    }
                                                    setRetryState={setRetryState}
                                                    acceptDocumentSend={
                                                        acceptDocumentSend
                                                    }
                                                    isCaseDetails={!submittedCase}
                                                    handleDocReUpload={
                                                        onDocReUpload
                                                    }
                                                    isDocReUploadInProgress={
                                                        isDocReUploadInProgress
                                                    }
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {!(fromDL && status === SubmittedCaseOrderStatus.aborted) && (
                                    <div className="case-footer-grid mt-30 flex-nowrap">
                                        <div
                                            className="signature-verified-row"
                                        >
                                            <label htmlFor="signature-verified">
                                                {t(
                                                    'CaseDetails.SingleCase.SignatureVerification.Label',
                                                )}
                                            </label>
                                            <Checkbox
                                                id="signature-verified"
                                                onChange={onSignatureVerify}
                                                checked={isSignatureVerified}
                                                disabled={
                                                    isSignatureVerified
                                                || isSignatureVerifiedInProgress
                                                }
                                                label={t(
                                                    'CaseDetails.SingleCase.SignatureVerification.Checkbox.Label',
                                                )}
                                            />
                                        </div>
                                    </div>
                                )}

                                {!fromDL ? (
                                    <div className="case-info-grid mt-32">
                                        <div className="case-info-grid-col">
                                            <Title type="5">
                                                {t('portfolios.columns.status')}
                                            </Title>
                                            {t(`DL.ViewCases.Status.${status}`)}
                                        </div>

                                        <Divider direction="vertical" />

                                        <div className="case-info-grid-col">
                                            <Title type="5">
                                                {t(
                                                    'ApplicationDetails.AdvisorySection.ProductApplications.Submitted',
                                                )}
                                            </Title>
                                            {submitted || '-'}
                                        </div>
                                        <div className="case-info-grid-col">
                                            <Title type="5">
                                                {t(
                                                    'ApplicationDetails.AdvisorySection.ProductApplications.SubmittedBy',
                                                )}
                                            </Title>
                                            {submittedBy || '-'}
                                        </div>
                                        {status !== SubmittedCaseOrderStatus.aborted && (
                                            <div className="case-info-grid-col align-end">
                                                <Button
                                                    type="link"
                                                    className="view-transactions-button"
                                                    onClick={handleViewTransactions}
                                                >
                                                    {t(
                                                        'CaseDetails.Link.ViewTransactions.Label',
                                                    )}
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    ''
                                )}

                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default React.memo(SingleCaseCompSubmitted);
